import React, { useMemo } from 'react'
import { Tooltip } from 'antd'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { compose } from 'redux'
import { IconPearDeck, IconPearAssessmentTest } from '@edulastic/icons'
import {
  StyledCollapse,
  SoloGroupAssignShell,
  GroupHeaderContainer,
  GroupHeaderFirstLine,
  GroupNameDisplay,
  GroupNameBadge,
  GroupHeaderStudentNames,
} from './styled'
import SubGroupCard from './SubGroupCard'
import {
  FOLLOW_UP_TYPE,
  GROUP_AND_ASSIGN_GROUP_HEADER_STUDENT_DISPLAY_MAX,
} from './constants'
import { getQuestionsCountFromItemGroups } from './utils'
import {
  getGroupIdForRecommendations,
  getStandardGroupIsMastered,
  getStandardGroupThreshold,
} from './groupAndAssignUtils'
import {
  getRecommendedTestsMapSelector,
  getRecommendedDecksMapSelector,
} from '../../../TestList/ducks'

const { Panel } = StyledCollapse

const GroupAccordion = ({
  group,
  recommendedTestsByGroupId = {},
  recommendedDecksByGroupId = {},
  recommendedTestsLoading,
  recommendedDecksLoading,
  soloSingleStudentView = false,
  includeTestContext = false,
  defaultGrades = [],
  defaultSubjects = [],
  curriculumId = null,
}) => {
  const {
    _id,
    name,
    studentCount = 0,
    standardGroups = [],
    bgColor,
    textColor,
  } = group

  const groupName = name
  const standardsCount = standardGroups?.length ?? 0
  const standardLabel = standardsCount === 1 ? 'standard' : 'standards'
  const explanation = `${studentCount} Students in ${standardsCount} ${standardLabel}`

  const uniqueStudentNames = useMemo(() => {
    const seen = new Set()
    const names = []
    ;(standardGroups || []).forEach((sg) => {
      ;(sg.students || []).forEach((s) => {
        const id = s._id
        if (id && !seen.has(id)) {
          seen.add(id)
          const n = s.name || ''
          if (n) names.push(n)
        }
      })
    })
    return names
  }, [standardGroups])

  const renderHeader = () => {
    const nameDisplayMax = GROUP_AND_ASSIGN_GROUP_HEADER_STUDENT_DISPLAY_MAX
    const visibleHeaderNames = uniqueStudentNames.slice(0, nameDisplayMax)
    const overflowHeaderNames = uniqueStudentNames.slice(nameDisplayMax)

    return (
      <GroupHeaderContainer>
        <GroupHeaderFirstLine>
          <GroupNameDisplay>
            <GroupNameBadge $bgColor={bgColor} $textColor={textColor}>
              {groupName}
            </GroupNameBadge>
            <span style={{ fontWeight: '600', margin: '0 5px' }}>
              {explanation}
            </span>
          </GroupNameDisplay>
        </GroupHeaderFirstLine>
        {uniqueStudentNames.length > 0 && (
          <GroupHeaderStudentNames>
            {visibleHeaderNames.join(', ')}
            {overflowHeaderNames.length > 0 && (
              <>
                {', '}
                <Tooltip title={overflowHeaderNames.join(', ')} placement="top">
                  <span
                    style={{
                      fontWeight: 600,
                    }}
                  >
                    +{overflowHeaderNames.length} more
                  </span>
                </Tooltip>
              </>
            )}
          </GroupHeaderStudentNames>
        )}
      </GroupHeaderContainer>
    )
  }

  const getRecommendedTestsForSubGroup = (standardGroup) => {
    const groupId = getGroupIdForRecommendations(group)
    const standardId = standardGroup._id
    if (!groupId || !standardId) return []

    const compositeGroupId = `${groupId}_${standardId}`
    const recommendations = recommendedTestsByGroupId[compositeGroupId] || []

    return recommendations.map((test) => {
      const itemCount =
        test.itemCount ??
        test.questionsCount ??
        (test.itemGroups ? getQuestionsCountFromItemGroups(test.itemGroups) : 0)
      return {
        _id: test._id,
        title: test.title,
        icon: <IconPearAssessmentTest />,
        type: FOLLOW_UP_TYPE.PEAR_ASSESSMENT_TEST,
        itemsCountText: itemCount ? ` ${itemCount} questions` : '',
        testData: {
          ...test,
          metadata: {
            testId: test._id,
            groupName,
            standardIds: [standardGroup._id],
          },
        },
      }
    })
  }

  const getRecommendedDecksForSubGroup = (standardGroup) => {
    const groupId = getGroupIdForRecommendations(group)
    const standardId = standardGroup._id
    if (!groupId || !standardId) return []

    const compositeGroupId = `${groupId}_${standardId}`
    const recommendations = recommendedDecksByGroupId[compositeGroupId] || []

    return recommendations.map((deck) => ({
      _id: deck._id,
      title: deck.title,
      url: deck.previewURL,
      icon: <IconPearDeck />,
      type: FOLLOW_UP_TYPE.PEAR_DECK,
      itemsCountText: deck.itemCount ? ` ${deck.itemCount} slides` : '',
    }))
  }

  const subGroups = standardGroups

  const subGroupCards = subGroups.map((standardGroup, index) => {
    const subGroupRecommendedTests = getRecommendedTestsForSubGroup(
      standardGroup
    )
    const subGroupRecommendedDecks = getRecommendedDecksForSubGroup(
      standardGroup
    )
    const isMastered = getStandardGroupIsMastered(standardGroup)
    const threshold = getStandardGroupThreshold(standardGroup)
    const isLowestBand = !isMastered && !threshold

    const subGroupDefaultGrades =
      Array.isArray(standardGroup.grades) && standardGroup.grades.length
        ? standardGroup.grades
        : defaultGrades

    const subGroupFollowUps = isMastered
      ? [...subGroupRecommendedTests]
      : subGroupRecommendedDecks.length || subGroupRecommendedTests.length
      ? [
          ...(subGroupRecommendedDecks || []),
          ...(subGroupRecommendedTests || []),
        ].slice(0, 2)
      : []

    return (
      <SubGroupCard
        key={standardGroup._id || index}
        subGroup={standardGroup}
        followUps={subGroupFollowUps}
        group={group}
        isMastered={isMastered}
        isLowestBand={isLowestBand}
        recommendedTestsLoading={recommendedTestsLoading}
        recommendedDecksLoading={recommendedDecksLoading}
        soloSingleStudentView={soloSingleStudentView}
        includeTestContext={includeTestContext}
        defaultGrades={subGroupDefaultGrades}
        defaultSubjects={defaultSubjects}
        curriculumId={curriculumId}
      />
    )
  })

  if (soloSingleStudentView) {
    return <SoloGroupAssignShell>{subGroupCards}</SoloGroupAssignShell>
  }

  return (
    <StyledCollapse defaultActiveKey={[]} expandIconPosition="right">
      <Panel header={renderHeader()} key={_id}>
        {subGroupCards}
      </Panel>
    </StyledCollapse>
  )
}

export default compose(
  withRouter,
  connect((state) => ({
    recommendedTestsByGroupId: getRecommendedTestsMapSelector(state),
    recommendedDecksByGroupId: getRecommendedDecksMapSelector(state),
    recommendedTestsLoading: state.testList.recommendedTestsLoading,
    recommendedDecksLoading: state.testList.recommendedDecksLoading,
  }))
)(GroupAccordion)
